// src/components/HomeBanner/HomeBanner.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import "./HomeBanner.css";

import banner1 from "../../../assets/home/banner/homepage-banner1.png";
import banner2 from "../../../assets/home/banner/homepage-banner2.png";
import banner3 from "../../../assets/home/banner/homepage-banner3.png";

const slides = [
  {
    image: banner1,
    subtitle: "ASIC Design Leaders",
    title: ["Expert ASIC Design", "Tailored for Your Business"],
    description:
      "Leverage our extensive experience in RTL, IP development and low‑power design to ensure your next ASIC project excels in performance, power, and area.",
    buttonText: "View Design Services",
    route: "/services/asic-design",
  },
  {
    image: banner2,
    subtitle: "ASIC Verification Leaders",
    title: ["Rigorous ASIC Verification", "Built to Your Specs"],
    description:
      "From UVM-based testbenches to formal verification and coverage-driven regression, our verification team guarantees your design meets every requirement.",
    buttonText: "View Verification Services",
    route: "/services/asic-verification",
  },
  {
    image: banner3,
    subtitle: "Technologies We Use",
    title: ["Industry‑Leading Tools", "and Methodologies"],
    description:
      "We harness SystemVerilog, UVM, emulation platforms, CI/CD flows and formal tools to accelerate your development cycle and maximize quality.",
    buttonText: "View Our Technologies",
    route: "/technologies",
  },
];

export default function HomeBanner() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0); // 1 = next, -1 = prev
  const navigate = useNavigate();

  const handleNext = () => {
    setDirection(1);
    setCurrentIndex((i) => (i + 1) % slides.length);
  };
  const handlePrev = () => {
    setDirection(-1);
    setCurrentIndex((i) => (i - 1 + slides.length) % slides.length);
  };

  // bg slide variants
  const bgVariants = {
    enter: (dir) => ({
      x: dir > 0 ? "100%" : "-100%",
      opacity: 1,
    }),
    center: { x: "0%", opacity: 1 },
    exit: (dir) => ({
      x: dir > 0 ? "-100%" : "100%",
      opacity: 1,
    }),
  };

  const { image, subtitle, title, description, buttonText, route } =
    slides[currentIndex];

  return (
    <section className="home-banner">
      {/* sliding background */}
      <AnimatePresence custom={direction} initial={false}>
        <motion.div
          key={currentIndex}
          custom={direction}
          className="banner-bg"
          variants={bgVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            x: { duration: 1.2, ease: "easeInOut" },
            opacity: { duration: 0.5 },
          }}
          style={{ backgroundImage: `url(${image})` }}
        />
      </AnimatePresence>

      {/* blur + overlay layers */}
      <div
        className="banner-blur"
        style={{ backgroundImage: `url(${image})` }}
      />
      <div className="banner-overlay" />

      {/* content with its own fade */}
      <div className="banner-content">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <h5>{subtitle.toUpperCase()}</h5>
            <h1>
              {title[0]}
              <br />
              {title[1]}
            </h1>
            <p>{description}</p>
            <button
              className="quote-button"
              onClick={() => navigate(route)}
            >
              {buttonText}
            </button>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* navigation arrows */}
      <button
        className="arrow-left"
        onClick={handlePrev}
        aria-label="Previous"
      >
        <i className="fas fa-chevron-left" />
      </button>
      <button
        className="arrow-right"
        onClick={handleNext}
        aria-label="Next"
      >
        <i className="fas fa-chevron-right" />
      </button>
    </section>
  );
}